import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useControlFrameworksListQuery } from 'src/graphql/_generated';
import { CSkeleton, Poppins } from 'src/common';

const DisplayBox = styled.div<{ $hasError?: boolean; $isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 14.5px 30px;
  border: 1px solid ${({ theme, $hasError }) => ($hasError ? theme.colors.error : theme.colors.stroke)};
  border-radius: 3px;
  background-color: ${({ theme }) => theme.colors.cultured};
  color: ${({ theme }) => theme.colors.prussianBlue};
  font-size: 14px;
  font-weight: 500;
  box-sizing: border-box;
  span {
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 14px;
    padding: 10px 25px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 12px;
    padding: 8px 22px;
  }
`;

const FrameworkInfo = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

interface FrameworksControlViewProps {
  controlId?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export const FrameworksControlView: React.FC<FrameworksControlViewProps> = ({ controlId, isLoading, isDisabled }) => {
  const { data } = useControlFrameworksListQuery();

  const control_identifier = useMemo(() => {
    if (!controlId || !data?.controlFrameworks) return null;

    for (const framework of data.controlFrameworks) {
      const control = framework.frameworkControls.find((c) => c.id === controlId);
      if (control) {
        return control.control_identifier;
      }
    }
    return null;
  }, [controlId, data?.controlFrameworks]);

  if (isLoading) {
    return <CSkeleton height={49} />;
  }

  return (
    <DisplayBox data-cy="framework-control-display" $isDisabled={isDisabled}>
      {control_identifier ? (
        <FrameworkInfo>
          <div>{control_identifier}</div>
        </FrameworkInfo>
      ) : (
        <Poppins color="cflowerBlue" px={14}>
          No control selected
        </Poppins>
      )}
    </DisplayBox>
  );
};
